import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineSend } from 'react-icons/ai'
import { Btn } from '../../components'
import { newChat, IChatPostResponse } from '../../service'
import ChatMessage, { ErrorMessage, IMessage, LoadingMessage } from './ChatMessage'
import { IBrandForm } from '../../interfaces'
import { v6 as uuidv6 } from 'uuid'

// interface ChatResponse {
// 	responseId: string
// 	message: string
// }

export default function Chat(): JSX.Element {
	const { control, handleSubmit, reset, getValues } = useForm<IChatPostResponse>({
		defaultValues: {
			sessionId: '',
			message: '',
			responseId: '',
		},
	})

	const [messages, setMessages] = useState<IMessage[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [hasError, setHasError] = useState(false)
	const [lastResponseId, setLastResponseId] = useState(localStorage.getItem('lastResponseId'))
	const userRaw = localStorage.getItem('userValues')
	const user: IBrandForm = userRaw ? JSON.parse(userRaw) : null

	useEffect(() => {
		initChat()
		setupScrollInput()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	function initChat() {
		try {
			const chatId = localStorage.getItem('brandTechChatId')
			if (chatId) {
				reset({
					sessionId: chatId,
				})

				const historyRaw = localStorage.getItem('chatHistory')
				const history = historyRaw ? JSON.parse(historyRaw) : null
				if (history.length > 0) {
					setMessages(history)
				} else {
					setMessages([
						{
							agent: 'AI',
							value: `${user?.leadName ? `Olá ${user?.leadName}!` : 'Olá!'} Qual a sua dúvida sobre Brandtech?`,
						},
					])
					localStorage.setItem('chatHistory', JSON.stringify(messages))
				}
			} else {
				const newMsg: IMessage = {
					agent: 'AI',
					value: `${user?.leadName ? `Olá ${user?.leadName}!` : 'Olá!'} Qual a sua dúvida sobre Brandtech?`,
				}

				setMessages([newMsg])
				localStorage.setItem('chatHistory', JSON.stringify(newMsg))
				const uuid = uuidv6()
				localStorage.setItem('brandTechChatId', uuid)
			}
		} catch (error) {
			setHasError(true)
		}
	}

	function resetChat(): void {
		try {
			localStorage.removeItem('brandTechChatId')
			localStorage.removeItem('chatHistory')
			localStorage.removeItem('lastResponseId')
			setLastResponseId(null)
			setMessages([])
			reset({
				sessionId: undefined,
				message: undefined,
			})
			setIsLoading(false)
			setHasError(false)
			resetInput()
			initChat()
		} catch (error) {}
	}

	function resetInput(): void {
		const element = document.getElementById('chatinput')
		if (element) {
			element.style.height = '24px'
		}
	}

	function setupScrollInput() {
		function OnInput(this: HTMLElement) {
			this.style.height = '0'
			this.style.height = this.scrollHeight + 'px'
		}

		const element = document.getElementById('chatinput')
		if (element) {
			element.addEventListener('input', OnInput, false)
		}
	}

	useEffect(() => {
		scrollBottom()
	}, [messages])

	function scrollBottom(): void {
		const chat = document.getElementById('chatwindow')
		if (chat) {
			chat.scrollTo({
				top: chat.scrollHeight,
				behavior: 'smooth',
			})
		}
	}

	async function submit(values: IChatPostResponse) {
		if (values.message) {
			values.message = values.message.trim()
		}
		try {
			if (values.message) {
				let newHistory = messages.concat([
					{
						agent: 'USER',
						value: values.message!,
					},
				])
				setMessages(newHistory)
				reset({
					message: '',
				})
				resetInput()

				setIsLoading(true)

				const res = await newChat({
					sessionId: localStorage.getItem('brandTechChatId') || '',
					message: values.message,
					responseId: lastResponseId,
				})
				setLastResponseId(res.data.responseId)
				localStorage.setItem('lastResponseId', res.data.responseId)
				newHistory = newHistory.concat([
					{
						agent: 'AI',
						value: res.data.message,
						responseId: res.data.responseId || lastResponseId,
					},
				])

				setMessages(newHistory)
				localStorage.setItem('chatHistory', JSON.stringify(newHistory))
			}
			reset({
				sessionId: values.sessionId,
				message: getValues('message'),
			})
		} catch (error) {
			setHasError(true)
		} finally {
			setIsLoading(false)
		}
	}

	function enterPress(e: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (e.key === 'Enter' && e.shiftKey === false) {
			e.preventDefault()
			if (!isLoading) {
				handleSubmit(submit)()
			}
		}
	}

	return (
		<div className="chat-container flex flex-col w-full h-full max-h-full pt-4 pb-8 lg:px-40 md:px-20 px-8 flex-grow-0">
			<div className="w-full text-right pr-4">
				<Btn
					className="btn-no-flex"
					onClick={resetChat}
					type="button"
					variant="form-btn"
					height="2.313rem"
					width="7rem"
					placeholder="Novo chat"
				/>
			</div>

			{messages.length ? (
				<div id="chatwindow" className="w-full  h-full  flex flex-col gap-4 px-4 overflow-auto">
					{messages.map((m, index) => (
						<ChatMessage key={index} message={m} />
					))}
					{isLoading ? <LoadingMessage /> : null}
					{hasError ? <ErrorMessage /> : null}
					<div className="min-h-[4rem]"></div>
				</div>
			) : (
				<div className="h-full w-full flex flex-col items-center justify-center">
					<img className="h-1/3 w-1/3" src="/assets/icons/minimal.svg" alt="GHBranding" title="GHBranding" />
				</div>
			)}

			{!hasError ? (
				<>
					<form className="chat-input-container" onSubmit={handleSubmit(submit)}>
						<div className="bg-white border border-black/10 dark:border-gray-900/50 dark:shadow-xs flex flex-col flex-grow md:pl-4 md:py-4 py-[10px] relative rounded-xl shadow-xs w-full">
							<Controller
								control={control}
								name="message"
								render={({ field: { onChange, value } }) => (
									<textarea
										autoFocus={true}
										id="chatinput"
										onKeyDown={enterPress}
										placeholder="Envie uma mensagem"
										onChange={onChange}
										value={value || ''}
										rows={1}
										className="input m-0 w-full resize-none border-0 bg-transparent p-0 pr-10 focus:ring-0 focus-visible:ring-0 dark:bg-transparent md:pr-12 pl-3 md:pl-0"
										style={{
											maxHeight: '8rem',
											height: '24px',
										}}
									></textarea>
								)}
							/>
							<button
								disabled={isLoading}
								type="submit"
								className="reset-button disabled:hover:cursor-not-allowed absolute p-1 rounded-md md:bottom-2 md:p-2 md:right-3 right-2 disabled:text-gray-400 enabled:bg-brand-purple text-white bottom-2.5 transition-colors disabled:opacity-40"
							>
								<AiOutlineSend color="black" size="1.5rem" />
							</button>
						</div>
					</form>
				</>
			) : null}
		</div>
	)
}
